import { VERSION_INPUT_TYPE, VersionInputOption } from "components/FormFields/VersionInput/types";
import { OPENTOFU_WORKFLOW_TOOL_OPTIONS } from "constants/opentofuWorkflowTools";
import { EMPTY_STACK_CONFIG_VENDOR_INPUT } from "constants/stack";
import { VENDOR_DICTIONARY, VENDOR_KEY } from "constants/vendor";
import {
  OpenTofuWorkflowTool,
  Stack,
  StackConfigVendorInput,
  StackConfigVendorOpenTofu,
} from "types/generated";
import { stackValidator } from "utils/formValidators";

import { type StackSettingsOpenTofuVendorFormFields } from "./Edit";

/**
 * The OpenTofu vendor config as returned by the `stackVendorConfig` fragment, where
 * `workflowTool` is aliased to `openTofuWorkflowTool` to avoid a GraphQL field merge
 * conflict with the Terraform vendor config (which also has a `workflowTool` field of a
 * different enum type).
 */
export type OpenTofuVendorConfig = Omit<StackConfigVendorOpenTofu, "workflowTool"> & {
  openTofuWorkflowTool: OpenTofuWorkflowTool;
};

export function workflowToolLabel(value: OpenTofuWorkflowTool): string {
  return OPENTOFU_WORKFLOW_TOOL_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export const getFormDefaultValues = (
  stackSettings: Stack
): StackSettingsOpenTofuVendorFormFields => {
  const config = stackSettings.vendorConfig as unknown as OpenTofuVendorConfig;

  return {
    version: {
      type: config.effectiveVersion ? VERSION_INPUT_TYPE.RANGE : VERSION_INPUT_TYPE.SPECIFIC,
      value: config.version || config.effectiveVersion || "",
    },
    useSmartSanitization: config.useSmartSanitization,
    workflowTool: config.openTofuWorkflowTool,
    externalStateAccessEnabled: config.externalStateAccessEnabled,
    workspace: config.workspace || "",
    concise: config.concise,
    enableCPUProfiling: config.enableCPUProfiling ?? true,
  };
};

export const getValidateToolVersion =
  (toolVersions?: string[]) => (version: VersionInputOption) => {
    if (!toolVersions || !version.value?.trim()) {
      return "Version is required";
    }

    if (version.type === VERSION_INPUT_TYPE.RANGE) {
      const rangeError = stackValidator.validateVersionRange(
        version.value,
        toolVersions,
        VENDOR_DICTIONARY.OPEN_TOFU
      );

      if (rangeError) {
        return rangeError;
      }
    }

    return true;
  };

export const mapCreateStackVendorUpdateInput = (
  formData: StackSettingsOpenTofuVendorFormFields
): StackConfigVendorInput => ({
  ...EMPTY_STACK_CONFIG_VENDOR_INPUT,
  [VENDOR_KEY.OPEN_TOFU]: {
    ...formData,
    version: formData.version.value || null,
    workspace: formData.workspace || null,
  },
});
